// src/oura/newscan/fixtures/firstCheck.ts
//
// A signed client's First AI check draft (JOURNEY-SPEC v1.1 §2.5, §5;
// handoffs H3-a and H5-a), as the fixture engine drafts and reviews it:
//
//   firstCheckBusiness   the Business step: the pitched inputs when the pitch
//                        is copied, else the site read with the form, each
//                        item tagged with where it came from
//   firstCheckDecisions  the form cards, the quote's outlet card (or a
//                        chain's cap card) and the pitch line
//   planDecisions        "For the plan": priority keywords and outlet names
//   changedVsPitch       how many of the 50 differ from the pitched 50
//
// Every card changes only the rows that use its service or outlet; none
// offers "draft again". Pure: the store keeps the state.

import type { ScanDraftBusiness, ScanDraftDecision, ScanDraftDetected, ScanDraftOutlet, ScanDraftPitch } from "@service/aeo/scanDraftTypes";
import type { AssembledRow, DraftState, FirstCheckFacts } from "./model";
import { SERVICE_SWAP_TARGETS, outletShares } from "./recipe";
import { canonicalTown, townForArea } from "../towns";
import type { WordSource } from "./words";
import type { LintIssue } from "./lint";
import { formAnswerDecisions } from "./formCards";

/** call 14: at most 9 outlets get their own location questions. */
export const OUTLET_CAP = 9;
/** The location questions the outlets share (Local v4.5, the v4.2 problem-led questions unchanged). */
const LOCATION_TOTAL = 26;
/** Priority keywords the plan needs. */
export const PLAN_KEYWORDS_MIN = 5;
export const PLAN_KEYWORDS_MAX = 10;

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "15 Oct", local time. */
export function dayMon(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : `${d.getDate()} ${MON[d.getMonth()]}`;
}

/** "Sarah" for "Sarah Wong", "Aaron" for "Dr Aaron Lim". */
export function firstName(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    const rest = /^(dr|dr\.|datin|datuk|dato'?|mr|mrs|ms|puan|encik)$/i.test(words[0] ?? "") ? words.slice(1) : words;
    return rest[0] ?? name.trim();
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
/** Loose match: "Braces" and "braces", "Teeth whitening" and "teeth whitening treatment". */
const stem = (s: string) => norm(s).replace(/(es|s)$/, "");
export const sameThing = (a: string, b: string): boolean => {
    const x = stem(a);
    const y = stem(b);
    return Boolean(x && y) && (x === y || x.includes(y) || y.includes(x));
};

/** The outlets the 50 ask about: the site's and the confirmed ones (render.ts activePlaces). */
export function activeOutlets(b: ScanDraftBusiness): ScanDraftOutlet[] {
    if (b.buy === "online") return [];
    return b.outlets.filter((o) => o.name.trim() && (o.source === "site" || o.confirmed === true));
}

/* ------------------------------------------------------------------------- *
 * The Business step
 * ------------------------------------------------------------------------- */

/**
 * The business a first_check draft opens with.
 *
 * With a pitch to copy: the pitched inputs, every outlet and service tagged
 * `scan` ("From the pitch") and counted as approved. The form's differences
 * come as cards, never as silent edits.
 *
 * Without: the site read, the form and the quote. A service the form also
 * lists is tagged `form`; an outlet the site does not name comes from the
 * form's areas (never a town), else the quote's (`quote`). A chain over the
 * cap keeps the flagship and the next 8 in the site's order.
 */
export function firstCheckBusiness(input: {
    pitch: ScanDraftBusiness | null;
    read: ScanDraftDetected;
    facts: Pick<FirstCheckFacts, "formServices" | "formAreas"> & { quoteAreas: string[] };
    businessName: string;
    buy: ScanDraftBusiness["buy"] | null;
}): ScanDraftBusiness {
    const { pitch, read, facts } = input;
    if (pitch) {
        return {
            ...JSON.parse(JSON.stringify(pitch)),
            businessName: input.businessName || pitch.businessName,
            outlets: pitch.outlets.map((o) => ({ name: o.name, town: o.town, source: "scan" as const, confirmed: true })),
            services: pitch.services.map((s) => ({ name: s.name, source: "scan" as const })),
            requestedBy: null,
        };
    }
    const buy = input.buy ?? read.buyGuess ?? "visit";
    const city = read.city || "";
    let outlets: ScanDraftOutlet[] = read.outlets.map((o) => ({ name: o.name, town: o.town || city, source: "site" as const }));
    if (!outlets.length) {
        const fromForm = facts.formAreas.filter((a) => a.trim() && !canonicalTown(a));
        const fromQuote = facts.quoteAreas.filter((a) => a.trim() && !canonicalTown(a));
        outlets = fromForm.length
            ? fromForm.map((name) => ({ name, town: townForArea(name) ?? (city || name), source: "form" as const, confirmed: true }))
            : fromQuote.map((name) => ({ name, town: townForArea(name) ?? (city || name), source: "quote" as const, confirmed: true }));
    }
    if (outlets.length > OUTLET_CAP) outlets = outlets.slice(0, OUTLET_CAP);
    // The site's services, tagged `form` when the form lists them too; a site the read could not use takes the form's.
    const services = read.services.length
        ? read.services.map((name) => ({ name, source: facts.formServices.some((f) => sameThing(f, name)) ? ("form" as const) : ("site" as const) }))
        : facts.formServices.filter((name) => name.trim()).map((name) => ({ name, source: "form" as const }));
    return {
        businessName: input.businessName || read.businessName,
        category: read.category,
        buy,
        market: read.market,
        city,
        outlets: buy === "online" ? [] : outlets,
        services,
        languages: { ms: "auto", zh: "auto" },
        rivals: [],
        otherNames: [],
        notUs: [],
        requestedBy: null,
    };
}

/* ------------------------------------------------------------------------- *
 * The cards
 * ------------------------------------------------------------------------- */

const slugOf = (s: string) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

function card(
    type: ScanDraftDecision["type"],
    key: string,
    severity: ScanDraftDecision["severity"],
    fields: Partial<Omit<ScanDraftDecision, "id" | "type" | "severity">> & { title: string },
): ScanDraftDecision {
    return {
        id: `${type}:${key}`,
        type,
        severity,
        detail: null,
        quotes: [],
        slots: [],
        state: "open",
        recommended: null,
        alternatives: [],
        evidence: null,
        ...fields,
    };
}

export interface FirstCheckDecisionInput {
    facts: FirstCheckFacts;
    /** The business name's distinctive words: a keyword never carries one. */
    nameTokens: string[];
    business: ScanDraftBusiness;
    rows: AssembledRow[];
    src: WordSource;
    forPlan: DraftState["forPlan"];
    changedVsPitch: number | null;
    /** Form services staff chose to leave out ("Leave it out"): never offered as a priority keyword. */
    leftOut?: string[];
    /** The lint's findings: a customer question swaps out the weakest row of its group. */
    issues?: LintIssue[];
    /** Answers so far, by decision id. */
    answered?: Record<string, "dismissed" | "acked" | "resolved">;
    /** The pitch scan this draft copied is still running ("Use the pitch scan"). */
    pitchRunning?: boolean;
}

/**
 * The service word "Add it" takes over: the first swap target the 50 use whose
 * value is not a service the client listed (`protect`), so a second "Add it"
 * never writes over the first. `current` is the value it replaces.
 */
export function serviceTarget(rows: AssembledRow[], protect: string[] = []): { variable: string; uses: number[]; current: string } | null {
    for (const v of SERVICE_SWAP_TARGETS) {
        const using = rows.filter((r) => !r.question.locked && r.question.fill_vars.includes(v));
        if (!using.length) continue;
        const current = (using[0].question.fill_values[v] ?? "").trim();
        if (current && protect.some((p) => sameThing(p, current))) continue;
        return { variable: v, uses: using.map((r) => r.question.n), current };
    }
    return null;
}

/** The services the client's form listed, already in the 50: "Add it" leaves their words alone. */
export const formServicesOf = (business: ScanDraftBusiness): string[] => business.services.filter((x) => x.source === "form").map((x) => x.name);

/** "questions 14 and 22". */
const questionList = (ns: number[]) =>
    ns.length === 1 ? `question ${ns[0]}` : `questions ${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A service the 50 already ask about: on the business, or a word value the rows use. */
function serviceAsked(name: string, input: FirstCheckDecisionInput): boolean {
    if (input.business.services.some((s) => sameThing(s.name, name))) return true;
    return input.rows.some((r) => r.question.fill_vars.some((v) => sameThing(r.question.fill_values[v] ?? "", name)));
}

/** An area the 50 already ask about: an outlet by name, or (a town) an outlet in that town. */
function areaAsked(area: string, business: ScanDraftBusiness): boolean {
    const town = canonicalTown(area);
    return business.outlets.some((o) => norm(o.name) === norm(area) || (town !== null && canonicalTown(o.town) === town));
}

/** The form cards and the quote's outlet card, in the order the Review shows them. */
export function firstCheckDecisions(input: FirstCheckDecisionInput): ScanDraftDecision[] {
    const { facts, business } = input;
    // An Online business has no outlets to decide, but its pitch, its services and the rest of
    // the form are still decided: returning nothing here dropped them, so the form could
    // disagree with the pitch or the draft without staff being asked (Jack, #4285).
    const outletsOn = business.buy !== "online";
    const who = firstName(facts.contactName || "The client");
    const origin = `From ${who}'s form, ${dayMon(facts.answeredAt)}`;
    const asPitched = facts.pitch ? "The rest stay as pitched." : "The rest stay as drafted.";
    const out: ScanDraftDecision[] = [];
    const active = outletsOn ? activeOutlets(business) : [];
    const flagship = active[0]?.name ?? business.city;

    // The pitch on these same 50 is still with the engines: its run can be the first check (H4-e).
    const pitchId = "pitch_running:pitch";
    if (facts.pitch && input.changedVsPitch === 0 && (input.pitchRunning || input.answered?.[pitchId])) {
        out.push(
            card("pitch_running", "pitch", "decide", {
                title: "The pitch scan on these 50 is still running",
                detail: "Use it as the first AI check when it finishes? Nothing new is paid, and its results become the baseline.",
                recommended: { label: "Use the pitch scan", action: { kind: "use_pitch_run" } },
                alternatives: [{ label: "Start a new one", action: { kind: "dismiss" } }],
            }),
        );
    }

    // Services the form lists that no question asks about.
    const target = serviceTarget(input.rows, formServicesOf(business));
    facts.formServices
        .filter((name) => name.trim() && !serviceAsked(name, input))
        .forEach((name) => {
            const n = target?.uses.length ?? 0;
            // Say what it replaces: every "Add it" names the same next free place until one is taken.
            const replaces = target?.current ? ` in place of “${target.current}”` : "";
            out.push(
                card("form_service_new", slugOf(name), "decide", {
                    title: `${who} lists ${name}, not in the 50`,
                    detail: n && target
                        ? `Adding it asks about it in ${questionList(target.uses)}${replaces}. ${asPitched}`
                        : `No question has room for it. ${asPitched}`,
                    slots: target?.uses ?? [],
                    origin,
                    recommended: target ? { label: "Add it", action: { kind: "add_service", name } } : null,
                    alternatives: [{ label: "Leave it out", action: { kind: "dismiss" } }],
                }),
            );
        });

    // Pitched services the form does not list (only when the form lists any).
    if (facts.formServices.length) {
        facts.pitchServices
            .filter((name) => business.services.some((s) => sameThing(s.name, name)))
            .filter((name) => !facts.formServices.some((f) => sameThing(f, name)))
            .forEach((name) => {
                const slots = input.rows.filter((r) => r.question.fill_vars.some((v) => sameThing(r.question.fill_values[v] ?? "", name))).map((r) => r.question.n);
                out.push(
                    card("form_service_missing", slugOf(name), "decide", {
                        title: `${who} does not list ${name}`,
                        detail: `The pitch asks about it in ${plural(slots.length, "question")}.`,
                        slots,
                        origin,
                        recommended: { label: "Keep it", action: { kind: "record" } },
                        alternatives: [{ label: "Drop it", action: { kind: "drop_service", name } }],
                    }),
                );
            });
    }

    // Everything below is about outlets: an Online business has none.
    if (outletsOn) {
        // The quote's outlet count, said on an outlet card that would go past it (Dana, 15 Oct).
        const soldOutlets = facts.quote?.outlets ?? 0;
        const quoteCovers =
            soldOutlets > 0 && active.length + 1 > soldOutlets
                ? ` The quote covers ${plural(soldOutlets, "outlet")}${facts.quote?.ref ? ` (${facts.quote.ref})` : ""}.`
                : "";

        // Areas the form names that no outlet covers. A town is never an outlet: name the area first.
        facts.formAreas
            .filter((area) => area.trim() && !areaAsked(area, business))
            .forEach((area) => {
                const town = canonicalTown(area);
                const shares = outletShares(active.length + 1, LOCATION_TOTAL);
                out.push(
                    card("form_outlet_new", slugOf(area), "decide", {
                        title: `${who} names an outlet in ${area}`,
                        detail: town
                            ? `${area} is a town, not the outlet's area. Name the part of ${area} it is in, and it gets its own questions.`
                            : `It gets its own ${shares[shares.length - 1] ?? 0} questions; ${flagship} keeps ${shares[0] ?? 0}.${quoteCovers}`,
                        origin,
                        recommended: town
                            ? { label: "Name the area", action: { kind: "open_business", area } }
                            : { label: `Add ${area}`, action: { kind: "add_outlet", name: area, town: townForArea(area) ?? (business.city || area), source: "form" } },
                        alternatives: [{ label: "Not an outlet", action: { kind: "dismiss" } }],
                    }),
                );
            });

        // Pitched outlets the form does not list (only when the form lists any). With no pitch, the
        // outlets after the flagship that the form does not list, as the backend asks (_form_cards).
        if (facts.formAreas.length) {
            const asked = facts.pitchOutlets.length
                ? facts.pitchOutlets
                : business.outlets.slice(1).filter((o) => o.source !== "form").map((o) => o.name);
            asked
                .filter((name) => business.outlets.some((o) => norm(o.name) === norm(name)))
                .filter((name) => !facts.formAreas.some((a) => norm(a) === norm(name)))
                .forEach((name) => {
                    const left = outletShares(Math.max(active.length - 1, 1), LOCATION_TOTAL)[0] ?? 0;
                    out.push(
                        card("form_outlet_missing", slugOf(name), "decide", {
                            title: `${who} does not list ${name}`,
                            detail: `${facts.pitchOutlets.length ? "The pitch asked about it" : "The website names it"}. Leave it out and ${flagship === name ? active[1]?.name ?? business.city : flagship} takes ${left}.`,
                            origin,
                            recommended: { label: "Keep it, it is open", action: { kind: "record" } },
                            alternatives: [{ label: "Leave it out", action: { kind: "drop_outlet", name } }],
                        }),
                    );
                });
        }

        // Names the site uses that are not outlets (the backend's outlet_undeclared).
        (facts.siteNames ?? [])
            .filter((name) => name.trim() && !business.outlets.some((o) => norm(o.name) === norm(name)))
            .forEach((name) => {
                out.push(
                    card("outlet_undeclared", slugOf(name), "decide", {
                        title: `The site names ${name}, which is not in your outlets`,
                        detail: "Add it if customers can visit it.",
                        recommended: { label: `Add ${name}`, action: { kind: "add_outlet", name, town: townForArea(name) ?? business.city } },
                        alternatives: [{ label: "Not an outlet", action: { kind: "dismiss" } }],
                    }),
                );
            });

        // The quote against the 50: a chain over the cap, else a different count.
        const sold = facts.quote?.outlets ?? 0;
        const m = active.length;
        const contracted = Math.max(sold, facts.planOutlets.length);
        // Every quote over the cap is decided, even with fewer than the cap's outlets active: a quote
        // for 20 with 8 active outlets used to raise neither card (Jack, #4291).
        if (facts.quote && contracted > OUTLET_CAP) {
            const fullSelection = m >= OUTLET_CAP;
            out.push(
                card("outlets_exceed_slots", "cap", "decide", {
                    title: `The quote sells ${contracted} outlets. ${OUTLET_CAP} get their own questions.`,
                    detail: fullSelection
                        ? `${flagship} and ${OUTLET_CAP - 1} more get location questions. The other ${contracted - OUTLET_CAP} are not asked about by name.`
                        : `Only ${plural(m, "active outlet")} ${m === 1 ? "is" : "are"} in these 50. Choose the ${OUTLET_CAP} outlets that get location questions.`,
                    recommended: fullSelection
                        ? { label: `Use these ${OUTLET_CAP}`, action: { kind: "record" } }
                        : { label: `Choose the ${OUTLET_CAP}`, action: { kind: "choose_outlets" } },
                    alternatives: fullSelection
                        ? [{ label: `Choose the ${OUTLET_CAP}`, action: { kind: "choose_outlets" } }]
                        : [{ label: "Review the outlets", action: { kind: "open_business" } }],
                }),
            );
        } else if (facts.quote && sold > 0 && sold !== m) {
            const shares = outletShares(sold, LOCATION_TOTAL);
            const named = [...facts.formAreas, ...facts.planOutlets].find((a) => a.trim() && !canonicalTown(a) && !areaAsked(a, business));
            out.push(
                card("outlets_vs_plan", "count", "decide", {
                    title: `The quote sells ${sold} outlets. These 50 ask about ${m}.`,
                    detail: `Quote ${facts.quote.ref} sold ${sold}. With ${sold}, ${flagship} gets ${shares[0] ?? 0} location questions and each branch ${shares[1] ?? shares[0] ?? 0}.`,
                    recommended:
                        named && sold > m
                            ? { label: `Add ${named}`, action: { kind: "add_outlet", name: named, town: business.city || named } }
                            : { label: "Add an outlet", action: { kind: "open_business" } },
                    alternatives: [{ label: `${m} is right`, action: { kind: "record" } }],
                }),
            );
        }
    }

    // The rest of the form: customer questions, goals and upcoming plans (H3-b).
    out.push(
        ...formAnswerDecisions({
            facts,
            business,
            rows: input.rows,
            src: input.src,
            issues: input.issues ?? [],
            who,
            origin,
            answered: input.answered ?? {},
        }),
    );
    return out;
}

/* ------------------------------------------------------------------------- *
 * For the plan
 * ------------------------------------------------------------------------- */

/** The business a plan keyword must not name: its full name and its distinctive words (the lint's name tokens). */
export interface PlanBrand {
    businessName: string;
    nameTokens: readonly string[];
}

const words = (s: string): string[] => norm(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/**
 * True when a keyword names the business: it holds the full business name as
 * whole words, or one of its distinctive name tokens as a word. The one rule
 * for the picks, the saved choice and the save action.
 */
export function keywordNamesBusiness(keyword: string, brand: PlanBrand): boolean {
    const k = words(keyword);
    if (!k.length) return false;
    const name = words(brand.businessName);
    if (name.length && ` ${k.join(" ")} `.includes(` ${name.join(" ")} `)) return true;
    return brand.nameTokens.some((t) => {
        const token = norm(t);
        return Boolean(token) && k.includes(token);
    });
}

export type PlanKeywordsCheck = { ok: true; keywords: string[] } | { ok: false; code: string; message: string };

/**
 * Whether a list of priority keywords is one the plan accepts. Each keyword is
 * trimmed with its spaces collapsed; then the list must have no blank, no
 * repeat (case and spacing ignored), no keyword that names the business, and
 * between PLAN_KEYWORDS_MIN and PLAN_KEYWORDS_MAX entries. Pure: the
 * approval gate (planDecisions) and the save action (store.applyPlanKeywords)
 * both call it, so a list the store refuses can never resolve the card.
 */
export function validPlanKeywords(list: readonly string[] | null | undefined, brand: PlanBrand): PlanKeywordsCheck {
    if (!list) return { ok: false, code: "keywords_count", message: `The plan needs ${PLAN_KEYWORDS_MIN} to ${PLAN_KEYWORDS_MAX} keywords.` };
    const keywords = list.map((k) => String(k ?? "").trim().replace(/\s+/g, " "));
    if (keywords.some((k) => !k)) return { ok: false, code: "keyword_blank", message: "One of the keywords is blank. Remove it or type a phrase." };
    const seen = new Set<string>();
    const twice = keywords.find((k) => seen.has(norm(k)) || !seen.add(norm(k)));
    if (twice) return { ok: false, code: "keyword_duplicate", message: `"${twice}" is in the list twice.` };
    const brandWord = keywords.find((k) => keywordNamesBusiness(k, brand));
    if (brandWord) return { ok: false, code: "keyword_is_brand", message: `"${brandWord}" names the business. A priority keyword must not.` };
    if (keywords.length < PLAN_KEYWORDS_MIN || keywords.length > PLAN_KEYWORDS_MAX)
        return { ok: false, code: "keywords_count", message: `The plan needs ${PLAN_KEYWORDS_MIN} to ${PLAN_KEYWORDS_MAX} keywords. This list has ${keywords.length}.` };
    return { ok: true, keywords };
}

/**
 * 5 to 10 phrases from the draft's service and area words and the form's
 * services, never the business name, no case duplicates.
 */
export function keywordPicks(input: { src: WordSource; business: ScanDraftBusiness; facts: FirstCheckFacts; nameTokens: string[] }): string[] {
    const { src, business, facts } = input;
    const area = activeOutlets(business)[0]?.name ?? business.city;
    const town = activeOutlets(business)[0]?.town ?? business.city;
    const place = src.value("place_noun") || src.value("category");
    const raw = [
        place && area ? `${place} ${area}` : "",
        src.value("core_service") && town ? `${src.value("core_service")} ${town}` : "",
        src.value("core_service"),
        src.value("secondary_service_1"),
        src.value("secondary_service_2"),
        ...facts.formServices.map((s) => s.toLowerCase()),
        src.value("secondary_service_3"),
        src.value("pain_point") && town ? `${src.value("pain_point")} ${town}` : "",
        src.value("condition"),
    ];
    const brand = { businessName: business.businessName, nameTokens: input.nameTokens };
    const seen = new Set<string>();
    return raw
        .map((k) => k.trim().replace(/\s+/g, " "))
        .filter((k) => k && !keywordNamesBusiness(k, brand))
        .filter((k) => !seen.has(norm(k)) && Boolean(seen.add(norm(k))))
        .slice(0, 8);
}

/**
 * The outlet names the plan card asks for: every active outlet (the ones the 50 ask about) and
 * every outlet the plan names, once each (compared with norm()), active outlets first. Neither
 * list alone is enough: the plan's list can be longer and still leave out an active outlet.
 */
export function planOutletNames(business: ScanDraftBusiness, facts: Pick<FirstCheckFacts, "planOutlets">): string[] {
    const seen = new Set<string>();
    return [...activeOutlets(business).map((o) => o.name), ...facts.planOutlets]
        .map((n) => n.trim().replace(/\s+/g, " "))
        .filter((n) => n && !seen.has(norm(n)) && Boolean(seen.add(norm(n))));
}

export type PlanOutletsCheck = { ok: true; names: string[] } | { ok: false; code: string; message: string };

/**
 * Whether a saved outlet-name list is exactly the plan's outlets (planOutletNames): each name
 * trimmed with its spaces collapsed, no blank, no repeat, nothing that is not one of them, and
 * every one of them present, in any order and case. Pure: the approval gate (planDecisions) and
 * the save action (store.applyPlanOutlets) both call it.
 */
export function validPlanOutletNames(list: readonly string[] | null | undefined, required: readonly string[]): PlanOutletsCheck {
    if (!list) return { ok: false, code: "outlet_names_missing", message: "Name every outlet in the plan." };
    const names = list.map((n) => String(n ?? "").trim().replace(/\s+/g, " "));
    if (names.some((n) => !n)) return { ok: false, code: "outlet_name_blank", message: "One of the outlet names is blank. Remove it or type the name." };
    const seen = new Set<string>();
    const twice = names.find((n) => seen.has(norm(n)) || !seen.add(norm(n)));
    if (twice) return { ok: false, code: "outlet_name_duplicate", message: `"${twice}" is in the list twice.` };
    const want = new Set(required.map(norm).filter(Boolean));
    const unrelated = names.find((n) => !want.has(norm(n)));
    if (unrelated) return { ok: false, code: "outlet_name_unknown", message: `"${unrelated}" is not one of the plan's outlets.` };
    const missing = required.filter((r) => norm(r) && !seen.has(norm(r)));
    if (missing.length) return { ok: false, code: "outlet_names_missing", message: `The list leaves out ${missing.join(", ")}. Every outlet in the plan needs its name.` };
    return { ok: true, names };
}

/** "Priority keywords for the plan" and "Outlet names for the plan". Approve waits for both. */
export function planDecisions(input: FirstCheckDecisionInput): ScanDraftDecision[] {
    const { facts, business } = input;
    const out: ScanDraftDecision[] = [];
    // The approval gate (Jack, #4285). Approve waits for both cards, and a saved list resolves its
    // card only when it is one the plan accepts. Both halves compare with the same norm() (trim,
    // collapse spaces, lower case):
    //   keywords  validPlanKeywords: no blank, no repeat, none naming the business (full name or
    //             a name token), PLAN_KEYWORDS_MIN to PLAN_KEYWORDS_MAX. The store's save action
    //             refuses the same lists, and "Use these N" is offered only on a valid set.
    //   outlets   validPlanOutletNames against planOutletNames (every active outlet plus every
    //             outlet the plan names): each covered exactly once, no blank, no repeat, nothing
    //             unrelated. The store's save action refuses the same lists.
    if (facts.heldKeywords.length < PLAN_KEYWORDS_MIN) {
        const brand = { businessName: business.businessName, nameTokens: input.nameTokens };
        const chosen = validPlanKeywords(input.forPlan?.priorityKeywords ?? null, brand);
        const chosenValid = chosen.ok;
        const left = (input.leftOut ?? []).map(norm);
        // Rebuilt on every read, so a service left out on its card drops out of the picks too.
        const generated = keywordPicks({ src: input.src, business, facts, nameTokens: input.nameTokens }).filter((k) => !left.some((l) => l && norm(k).includes(l)));
        const picks = chosen.ok ? chosen.keywords : generated;
        // Sparse inputs can generate fewer than the plan needs: then there is nothing to offer in one click.
        const picksValid = validPlanKeywords(picks, brand).ok;
        out.push(
            card("plan_keywords", "plan", "decide", {
                title: "Priority keywords for the plan",
                detail: `The plan needs ${PLAN_KEYWORDS_MIN} to ${PLAN_KEYWORDS_MAX}. Oura picked ${picks.length} from these 50 and the services.`,
                picks,
                state: chosenValid ? "resolved" : "open",
                recommended: picksValid ? { label: `Use these ${picks.length}`, action: { kind: "set_plan_keywords", keywords: picks } } : null,
                alternatives: [{ label: "Edit them", action: { kind: "edit_plan_keywords" } }],
            }),
        );
    }
    const names = planOutletNames(business, facts);
    if (names.length >= 2 && facts.heldOutletNames.length < names.length) {
        // Resolved only when the saved names cover every active and plan outlet exactly once (Jack, #4285).
        const chosen = validPlanOutletNames(input.forPlan?.outletNames ?? null, names);
        const chosenValid = chosen.ok;
        const picks = chosen.ok ? chosen.names : names;
        out.push(
            card("plan_outlet_names", "plan", "decide", {
                title: "Outlet names for the plan",
                detail: "Per-outlet tasks need them. These come from the outlets above.",
                picks,
                state: chosenValid ? "resolved" : "open",
                recommended: { label: "Use these", action: { kind: "set_plan_outlets", names: picks } },
                alternatives: [{ label: "Edit them", action: { kind: "edit_plan_outlets" } }],
            }),
        );
    }
    return out;
}

/* ------------------------------------------------------------------------- *
 * The pitch line
 * ------------------------------------------------------------------------- */

/** The Review's pitch line (`presale_set`, info): speaks of questions, never of the number. */
export function presaleLine(facts: FirstCheckFacts, changed: number | null, total: number): ScanDraftDecision {
    const pitch: ScanDraftPitch | null = facts.pitch;
    const on = pitch ? dayMon(pitch.scannedAt) : "";
    let title: string;
    if (!pitch) title = `Drafted from ${firstName(facts.contactName || "the client")}'s form and the site. The pitch was measured outside Oura.`;
    else if (!pitch.reusable || changed === null) title = `Drafted new on the current recipe. The pitch on ${on} used an older one, so the two are not like for like.`;
    else if (changed === 0) title = `Same 50 as the pitch on ${on}.`;
    else title = `Changes ${changed} of the 50 you pitched on ${on}. The other ${total - changed} compare like for like. Question history shows this as set v2.`;
    return card("presale_set", "pitch", "info", { title });
}

/** Rows that differ from the pitched 50 (a new row key counts). Null: not like for like. */
export function changedVsPitch(facts: FirstCheckFacts | null | undefined, rows: AssembledRow[]): number | null {
    if (!facts?.pitchTexts) return null;
    const texts = facts.pitchTexts;
    return rows.filter((r) => texts[r.plan.key] !== r.question.text).length;
}

